import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

const TAG_OPTIONS = [
  'sharing',
  'databases',
  'notion-ai',
  'agents',
  'permissions',
  'sprints',
  'mentions',
  'new-hire',
  'confluence',
  'legal',
] as const;
type Tag = (typeof TAG_OPTIONS)[number];

export type AskQuestionInput = {
  title: string;
  body: string;
  tags: Tag[];
};

type FormShape = { title: string; body: string };

type Props = {
  initial?: { title?: string; body?: string; tags?: Tag[] };
  busy?: boolean;
  onSubmit: (input: AskQuestionInput) => void;
  onClose: () => void;
};

export function AskQuestionModal({ initial, busy, onSubmit, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [tags, setTags] = useState<Set<Tag>>(new Set(initial?.tags ?? []));
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormShape>({
    defaultValues: { title: initial?.title ?? '', body: initial?.body ?? '' },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const titleLen = (watch('title') ?? '').length;
  const bodyLen = (watch('body') ?? '').length;

  const toggleTag = (t: Tag) => {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else if (next.size < 5) next.add(t);
      return next;
    });
  };

  const submit = handleSubmit((data) => {
    onSubmit({ title: data.title, body: data.body, tags: Array.from(tags) });
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-q-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: '32px 16px',
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 12,
          padding: 36,
          width: '100%',
          maxWidth: 620,
          color: 'var(--color-text)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow" style={{ color: 'var(--color-accent)' }}>▌ Ask a question</div>
        <h2 id="ask-q-title" style={{ fontSize: 24, fontWeight: 700, margin: '6px 0 6px' }}>
          What&apos;s on your mind?
        </h2>
        <p style={{ color: 'var(--color-text-3)', fontSize: 13, margin: '0 0 24px' }}>
          Async, by Vimers, for Vimers. Be specific so someone can actually help.
        </p>

        <form onSubmit={submit}>
          <Field
            label="Title"
            hint={`${titleLen} / 200 characters · min 8`}
            error={errors.title?.message}
          >
            <input
              type="text"
              {...register('title', {
                required: 'Title is required',
                minLength: { value: 8, message: 'At least 8 characters' },
                maxLength: { value: 200, message: 'At most 200 characters' },
              })}
              maxLength={200}
              placeholder="e.g. How do I share a database with just my own team?"
              style={inputStyle}
            />
          </Field>

          <Field
            label={
              <span>
                Body{' '}
                <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional, but recommended)</span>
              </span>
            }
            hint={`${bodyLen} / 5000 characters`}
          >
            <textarea
              rows={5}
              maxLength={5000}
              {...register('body', { maxLength: 5000 })}
              placeholder="What have you tried? What's the context? The more detail, the easier to answer."
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </Field>

          <Field
            label={<span>Tags <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(up to 5)</span></span>}
          >
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TAG_OPTIONS.map((t) => {
                const active = tags.has(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    style={{
                      padding: '6px 11px',
                      borderRadius: 999,
                      fontSize: 12,
                      cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
                      background: active ? 'var(--color-accent-soft)' : 'transparent',
                      color: active ? 'var(--color-accent)' : 'var(--color-text-2)',
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {active ? '✓ ' : ''}{t}
                  </button>
                );
              })}
            </div>
          </Field>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{ background: 'transparent', color: 'var(--color-text-3)', border: 0, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Posting…' : '▸ Post question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: React.ReactNode;
  hint?: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--color-text-2)' }}>
        {label}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: 11, color: 'var(--color-danger)', marginTop: 4 }}>{error}</div>
      )}
      {hint && !error && (
        <div style={{ fontSize: 11, color: 'var(--color-text-3)', marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--color-bg-deep)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: 13,
};
